import { useEffect } from 'react';
import { isApiUnreachableError } from '@wineryxplorer/shared';
import { trpc } from '../lib/trpc';
import { AuthContext } from './AuthContextDefinition';
import type { AuthContextType } from './AuthContextDefinition';

interface AuthProviderProps {
  children: React.ReactNode;
}

/** Attempts before falling back to the slower reconnect poll. */
const UNREACHABLE_RETRY_LIMIT = 3;

/** How often to re-ask `user.getMe` while the API is dark. */
const RECONNECT_POLL_MS = 5000;

export const AuthProvider = ({ children }: AuthProviderProps) => {
  // Try to get user from tRPC (real auth or dev auth via backend)
  const {
    data: serverUser,
    isPending,
    error,
    refetch,
  } = trpc.user.getMe.useQuery(undefined, {
    staleTime: 0, // Always allow refetch when requested
    gcTime: 10 * 60 * 1000, // 10 minutes
    // A 401 is a real answer — take it at face value and stop. A network
    // failure or 5xx is not an answer at all, so keep asking rather than
    // reporting the user as signed out (issue #246).
    retry: (failureCount, err) =>
      isApiUnreachableError(err) && failureCount < UNREACHABLE_RETRY_LIMIT,
    // Once those retries are spent and the API is still dark, poll until it
    // comes back so the offline banner clears on its own — no reload, no
    // re-login. React Query drops the interval as soon as a fetch succeeds.
    refetchInterval: (query) =>
      query.state.status === 'error' && isApiUnreachableError(query.state.error)
        ? RECONNECT_POLL_MS
        : false,
    // The tab regaining focus is the cheapest signal that the network may be
    // back, so use it even though the app disables this globally.
    refetchOnWindowFocus: true,
  });

  // Monitor dev auth changes and refetch user data
  useEffect(() => {
    const handleDevAuthChange = () => {
      // Refetch user data when dev auth changes
      // The backend will use the x-dev-user header to return the correct user
      refetch();
    };

    // Listen for dev auth changes (when user switches in dev toolbar)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'dev-auth-user') {
        handleDevAuthChange();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also listen for direct localStorage changes in same tab
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function (key: string, value: string) {
      originalSetItem.apply(this, [key, value]);
      if (key === 'dev-auth-user') {
        handleDevAuthChange();
      }
    };

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      localStorage.setItem = originalSetItem;
    };
  }, [refetch]);

  // User comes from server (either real auth or dev auth via backend).
  // React Query holds the last successful payload through a failed refetch, so
  // an outage never blanks the signed-in user out from under the route guards.
  const currentUser = serverUser ?? null;

  const isApiUnreachable = Boolean(error) && isApiUnreachableError(error);

  const contextValue: AuthContextType = {
    user: currentUser,
    // While the first `getMe` is still in flight we genuinely do not know who
    // the caller is. Reporting "loading" rather than "anonymous" is what keeps
    // AuthGuard from bouncing a signed-in user to /login before the answer
    // arrives — the old `&& hasDevUser` gate made this permanently false for
    // anyone using a real session.
    isLoading: isPending,
    isAuthenticated: Boolean(currentUser),
    isApiUnreachable,
    refetchUser: refetch,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
